import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  acceptAllConsent,
  getConsentSnapshot,
  rejectNonEssentialConsent,
  saveConsentPreferences,
  subscribeToConsentChanges,
  type ConsentPreferences,
} from '@services/consent';
import './ConsentManager.css';

const OPEN_CONSENT_PREFERENCES_EVENT = 'saf:open-consent-preferences';

export function openConsentPreferences(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_CONSENT_PREFERENCES_EVENT));
  }
}

function ConsentManager() {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState(() => getConsentSnapshot());
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [draft, setDraft] = useState<ConsentPreferences>(() => snapshot.preferences);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => subscribeToConsentChanges(setSnapshot), []);

  useEffect(() => {
    const open = () => {
      setDraft(getConsentSnapshot().preferences);
      setPreferencesOpen(true);
    };
    window.addEventListener(OPEN_CONSENT_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_PREFERENCES_EVENT, open);
  }, []);

  useEffect(() => {
    if (!preferencesOpen) return;

    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const frame = window.requestAnimationFrame(() => {
      dialogRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      previouslyFocusedRef.current?.focus();
    };
  }, [preferencesOpen]);

  const closePreferences = () => setPreferencesOpen(false);

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closePreferences();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;

    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const reject = () => {
    rejectNonEssentialConsent();
    closePreferences();
  };

  const accept = () => {
    acceptAllConsent();
    closePreferences();
  };

  const save = () => {
    saveConsentPreferences(draft);
    closePreferences();
  };

  return (
    <>
      {!snapshot.hasDecision && !preferencesOpen && (
        <section
          className="consent-banner"
          role="region"
          aria-label={t('consent.banner.label', 'Privacy choices')}
        >
          <div className="consent-banner__copy">
            <h2>{t('consent.banner.title', 'Your privacy choices')}</h2>
            <p>
              {t(
                'consent.banner.body',
                'We use necessary browser storage to provide SendAllFiles. Optional analytics and advertising measurement are used only with your permission.',
              )}
            </p>
            <div className="consent-banner__links">
              <Link to="/privacy">{t('consent.privacyLink', 'Privacy notice')}</Link>
              <Link to="/cookies">{t('consent.cookiesLink', 'Cookies and storage')}</Link>
            </div>
          </div>
          <div className="consent-banner__actions">
            <button type="button" className="consent-button consent-button--secondary" onClick={reject}>
              {t('consent.reject', 'Reject optional uses')}
            </button>
            <button
              type="button"
              className="consent-button consent-button--secondary"
              onClick={() => {
                setDraft(snapshot.preferences);
                setPreferencesOpen(true);
              }}
            >
              {t('consent.customize', 'Choose settings')}
            </button>
            <button type="button" className="consent-button consent-button--primary" onClick={accept}>
              {t('consent.acceptAll', 'Accept all')}
            </button>
          </div>
        </section>
      )}

      {preferencesOpen && (
        <div
          className="consent-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closePreferences();
          }}
        >
          <div
            ref={dialogRef}
            className="consent-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-dialog-title"
            aria-describedby="consent-dialog-description"
            onKeyDown={handleDialogKeyDown}
          >
            <div className="consent-dialog__header">
              <div>
                <h2 id="consent-dialog-title">
                  {t('consent.dialog.title', 'Privacy settings')}
                </h2>
                <p id="consent-dialog-description">
                  {t(
                    'consent.dialog.description',
                    'Choose which optional uses you allow. Necessary storage is always active.',
                  )}
                </p>
              </div>
              <button
                type="button"
                className="consent-dialog__close"
                onClick={closePreferences}
                aria-label={t('consent.dialog.close', 'Close privacy settings')}
              >
                ×
              </button>
            </div>

            <div className="consent-dialog__options">
              <div className="consent-option consent-option--required">
                <div>
                  <h3>{t('consent.necessary.title', 'Strictly necessary')}</h3>
                  <p>
                    {t(
                      'consent.necessary.description',
                      'Supports core service functions, security, and your saved choices.',
                    )}
                  </p>
                </div>
                <span>{t('consent.alwaysActive', 'Always active')}</span>
              </div>

              <label className="consent-option">
                <div>
                  <h3>{t('consent.analytics.title', 'Analytics')}</h3>
                  <p>
                    {t(
                      'consent.analytics.description',
                      'Helps us understand service performance and usage.',
                    )}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={draft.analytics}
                  onChange={(event) => setDraft((value) => ({ ...value, analytics: event.target.checked }))}
                />
              </label>

              <label className="consent-option">
                <div>
                  <h3>{t('consent.adsMeasurement.title', 'Advertising measurement')}</h3>
                  <p>
                    {t(
                      'consent.adsMeasurement.description',
                      'Allows optional advertising measurement. Personalized advertising remains disabled.',
                    )}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={draft.adsMeasurement}
                  onChange={(event) => setDraft((value) => ({ ...value, adsMeasurement: event.target.checked }))}
                />
              </label>
            </div>

            <p className="consent-dialog__details">
              <Link to="/privacy" onClick={closePreferences}>
                {t('consent.privacyLink', 'Privacy notice')}
              </Link>
              {' · '}
              <Link to="/cookies" onClick={closePreferences}>
                {t('consent.cookiesLink', 'Cookies and storage')}
              </Link>
            </p>

            <div className="consent-dialog__actions">
              <button type="button" className="consent-button consent-button--secondary" onClick={reject}>
                {t('consent.reject', 'Reject optional uses')}
              </button>
              <button type="button" className="consent-button consent-button--secondary" onClick={save}>
                {t('consent.save', 'Save choices')}
              </button>
              <button type="button" className="consent-button consent-button--primary" onClick={accept}>
                {t('consent.acceptAll', 'Accept all')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ConsentManager;
